import { useFetchHotel } from "./redux/fetchHotel";
import { getBookingStatusLabel } from "../../common/bookingStatus";
import React, { useEffect, useState, useCallback, useRef } from "react";
import {} from "./redux/hooks";
import {
  Box,
  Header,
  Heading,
  Button,
  DataTable,
  Text,
  Pagination,
} from "grommet";
import { Add, Edit, Trash } from "grommet-icons";
import { GuestService } from "../../services";
import { capitalize } from "@common/utils";
import { GuestModal, ImportBookingsButton } from "./";
import {
  BookingDates,
  AppInstalled,
  NoPhoneIcon,
  BookingStatus,
  NoResults,
  SmsUnreachableIcon,
} from "@shared/";
import { presentAlertPrompt } from "@shared/Alert";
import { presentToastSuccess, presentToastErrorContent } from "@shared/Toast";
import { presentLoading, dismissLoading } from "@shared/Loading";
import { CAN_IMPORT_FROM_PMS } from "@common/constants";
import { NoEmailIcon } from "@features/shared";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;

const mapBooking = (booking, serviceType, t) => ({
  id: booking._id,
  bookingId: booking.bookingId,
  name: capitalize(booking.firstname) + " " + capitalize(booking.lastname),
  appInstalled: booking.appInstalled,
  room: booking.roomNumber ? booking.roomNumber : "-",
  dates: (
    <BookingDates
      checkinDate={booking.checkinDate}
      checkoutDate={booking.checkoutDate}
    />
  ),
  status: booking.status,
  statusName: getBookingStatusLabel(booking.status, serviceType, t),
  phone: booking.phone,
  email: booking.email,
  smsUnreachable: !!booking.smsUnreachable,
  emailUnreachable: !!booking.emailUnreachable,
  commsFailed: !!booking.commsFailed,
});

export default function ReservationsPage() {
  const { hotel } = useFetchHotel();
  const [guestList, setGuestList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState({ field: null, term: "" });
  const searchDebounceRef = useRef(null);
  const [guestModal, setGuestModal] = useState(false);
  const [guestModalProps, setGuestModalProps] = useState({
    guestId: 0,
    sendKeys: false,
  });
  const { t } = useTranslation();

  const fetchGuests = useCallback(
    (page = 1, searchField = null, searchTerm = "") => {
      presentLoading();
      setLoading(true);
      const params = { page, limit: PAGE_SIZE };
      if (searchField && searchTerm.trim()) {
        params.searchField = searchField;
        params.searchTerm = searchTerm.trim();
      }
      GuestService.getBookings("pending", params).then(
        (response) => {
          const { data, total } = response.data;
          dismissLoading();
          setLoading(false);
          setGuestList(data.map(booking => mapBooking(booking, hotel?.serviceType, t)));
          setTotalItems(total);
        },
        (error) => {
          dismissLoading();
          setLoading(false);
          setGuestList([]);
          setTotalItems(0);
        },
      );
    },
    [hotel?.serviceType, t],
  );

  const columns = [
    {
      property: "bookingId",
      header: t("reservations.colRef"),
      search: true,
    },
    {
      property: "name",
      header: t("reservations.colName"),
      search: true,
      render: (datum) => (
        <Box direction="row" gap="small" align="center">
          <Text>{datum.name}</Text>
          {datum.appInstalled && <AppInstalled pkey={datum.id} />}
          {!datum.phone && !datum.appInstalled && (
            <NoPhoneIcon pkey={`nophone-${datum.id}`} />
          )}
          {datum.phone && datum.smsUnreachable && (
            <SmsUnreachableIcon
              pkey={`smsunreachable-${datum.id}`}
              phone={datum.phone}
              color={
                datum.email && !datum.emailUnreachable
                  ? "status-warning"
                  : "status-critical"
              }
            />
          )}
          {!datum.email && <NoEmailIcon pkey={`noemail-${datum.id}`} />}
        </Box>
      ),
    },
    {
      property: "room",
      header: t("reservations.colRoom"),
      align: "center",
      search: true,
    },
    {
      property: "dates",
      header: t("reservations.colDates"),
      // render: datum =>
      //   datum.date && new Date(datum.date).toLocaleDateString('en-US'),
      align: "center",
    },
    {
      property: "statusName",
      header: t("reservations.colStatus"),
      align: "center",
      search: true,
      render: (datum) => (
        <BookingStatus status={datum.status} commsFailed={datum.commsFailed} />
      ),
    },
    {
      property: "actions",
      header: t("reservations.colActions"),
      align: "center",
      render: (datum) => (
        <Box direction="row" gap="medium">
          <Button
            hoverIndicator="accent-4"
            icon={<Edit size="small" />}
            size="small"
            primary
            color="status-warning"
            onClick={() => handleGuestDetails(datum.id)}
          />
          {/* <Button
            hoverIndicator="accent-1"
            icon={<License size="small" />}
            size="small"
            primary
            color="status-ok"
            onClick={() => handleGuestModalKeys(datum.id)}
          /> */}
          <Button
            hoverIndicator="dark-1"
            icon={<Trash size="small" />}
            size="small"
            primary
            color="status-critical"
            onClick={() => handleRemoveBooking(datum.id)}
          />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchGuests(currentPage, search.field, search.term);
  }, [currentPage, search, fetchGuests]);

  const handleSearch = useCallback((searchObj) => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const entry = Object.entries(searchObj).find(([, v]) => v && v.trim());
      const field = entry ? entry[0] : null;
      const term = entry ? entry[1] : "";
      setSearch({ field, term });
      setCurrentPage(1);
    }, 400);
  }, []);

  const handlePageChange = ({ page }) => {
    setCurrentPage(page);
  };

  const handleAddGuest = () => {
    setGuestModalProps({ guestId: 0, sendKeys: false });
    setGuestModal(true);
  };

  // const handleGuestModalKeys = (guestId) => {
  //   setGuestModalProps({ guestId, sendKeys: true });
  //   setGuestModal(true);
  // };

  const handleGuestModalClose = (refresh) => {
    setGuestModal(false);
    if (refresh) fetchGuests(currentPage, search.field, search.term);
  };

  const handleGuestDetails = (guestId) => {
    window.location.href = "expected/" + guestId;
  };

  const handleRemoveBooking = (guestId) => {
    function removeGuest() {
      GuestService.deleteBooking(guestId).then(
        (response) => {
          presentToastSuccess(response.data.message);
          fetchGuests(currentPage, search.field, search.term);
        },
        (error) => {
          presentToastErrorContent(error);
          fetchGuests(currentPage, search.field, search.term);
        },
      );
    }

    presentAlertPrompt({
      title: t("reservations.deleteConfirmTitle"),
      message: t("reservations.deleteConfirmMessage"),
      onOK: removeGuest,
      buttonOKText: t("reservations.deleteButton"),
      buttonOKColor: "status-critical",
    });
  };

  return (
    <Box align="center" justify="start" fill pad="medium">
      {guestModal && (
        <GuestModal
          guestId={guestModalProps.guestId}
          onClose={handleGuestModalClose}
          sendKeys={guestModalProps.sendKeys}
        />
      )}
      <Header
        align="center"
        direction="row"
        justify="between"
        gap="medium"
        fill="horizontal"
      >
        <Heading level="2">
          {totalItems > 0
            ? t("reservations.titleWithCount", { count: totalItems })
            : t("reservations.title")}
        </Heading>
        <Box direction="row" gap="small">
          {CAN_IMPORT_FROM_PMS && (
            <ImportBookingsButton
              onComplete={() =>
                fetchGuests(currentPage, search.field, search.term)
              }
            />
          )}
          <Button
            icon={<Add size="small" />}
            label={t("reservations.addGuest")}
            primary
            onClick={handleAddGuest}
          />
        </Box>
      </Header>
      <Box fill="horizontal">
        {!loading && !guestList.length && !search.term ? (
          <NoResults>
            This list is empty. <br />
            Start adding or importing reservations.
          </NoResults>
        ) : (
          <Box>
            <DataTable
              columns={columns}
              data={guestList}
              primaryKey="id"
              onClickRow={(event) => {
                if (
                  event.target.localName !== "button" &&
                  event.target.localName !== "svg" &&
                  event.target.localName !== "path"
                )
                  handleGuestDetails(event.datum.id);
              }}
              onSearch={handleSearch}
            />
            {totalItems > PAGE_SIZE && (
              <Box align="center" pad={{ top: "medium", bottom: "80px" }}>
                <Pagination
                  numberItems={totalItems}
                  step={PAGE_SIZE}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

ReservationsPage.propTypes = {};
ReservationsPage.defaultProps = {};
